import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { scheduleData } from '../mockData';
import { Calendar, Clock, MapPin, Sparkles, Thermometer, Shirt, Phone, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

export const Schedule = () => {
  const { t } = useLanguage();
  
  // Helper function to get translated event data
  const getEventData = (dayId, eventIndex) => {
    const eventKey = `event${eventIndex + 1}`;
    const dayKey = `day${dayId}`;
    return {
      title: t(`schedule.events.${dayKey}.${eventKey}.title`),
      description: t(`schedule.events.${dayKey}.${eventKey}.description`),
      location: t(`schedule.events.${dayKey}.${eventKey}.location`)
    };
  };
  
  return (
    <div className="min-h-screen cream-bg">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-56 md:pt-48 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Calendar Icon - Inline SVG */}
          <div className="mb-8">
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#335B7A" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ display: 'block', margin: '0 auto' }}
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-semibold text-foreground mb-6">
            {t('schedule.title')}
          </h1>
          <p className="font-sans text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('schedule.subtitle')}
          </p>
        </div>
      </section>

      {/* Three Column Schedule */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {scheduleData.days.map((day, dayIndex) => (
              <div 
                key={day.id} 
                className={`fade-in ${day.isWeddingDay ? 'lg:scale-105' : ''}`}
                style={{ animationDelay: `${dayIndex * 0.15}s` }}
              >
                {/* Day Card */}
                <div className={`bg-white shadow-xl overflow-hidden border-2 ${
                  day.isWeddingDay ? 'border-primary' : 'border-border'
                } h-full flex flex-col`}>
                  {/* Day Header */}
                  <div className={`${
                    day.isWeddingDay 
                      ? 'blue-bg text-white' 
                      : 'cream-bg text-foreground'
                  } p-8 text-center relative`}>
                    {day.isWeddingDay && (
                      <div className="absolute top-4 right-4">
                        <Sparkles className="w-6 h-6 text-white animate-pulse" />
                      </div>
                    )}
                    <div className="text-sm font-sans font-semibold uppercase tracking-wider mb-2 opacity-90">
                      {day.dayNumber}
                    </div>
                    <div className="font-serif text-3xl font-bold mb-2">
                      {day.id === 1 ? t('schedule.dates.day1') : day.id === 2 ? t('schedule.dates.day2') : t('schedule.dates.day3')}
                    </div>
                    <div className={`text-base font-sans font-medium ${
                      day.isWeddingDay ? 'text-white' : 'blue-text'
                    }`}>
                      {day.id === 1 ? t('schedule.day1Title') : day.id === 2 ? t('schedule.day2Title') : t('schedule.day3Title')}
                    </div>
                  </div>

                  {/* Events Timeline */}
                  <div className="p-6 flex-1 cream-bg">
                    <div className="space-y-6">
                      {day.events.map((event, eventIndex) => {
                        const translatedEvent = getEventData(day.id, eventIndex);
                        return (
                          <div 
                            key={event.time}
                            className="relative pl-8 border-l-2 border-border pb-6 last:pb-0"
                          >
                            {/* Timeline dot */}
                            <div className="absolute -left-2 top-0 w-4 h-4 rounded-full blue-bg border-2 border-white shadow-sm" />
                            
                            {/* Time */}
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="w-4 h-4 blue-text" />
                              <span className="font-sans text-sm font-bold blue-text">
                                {event.time}
                              </span>
                            </div>
                            
                            {/* Event Title */}
                            <h3 className="font-serif text-lg font-semibold text-foreground mb-1">
                              {translatedEvent.title}
                            </h3>
                            
                            {/* Description */}
                            <p className="font-sans text-sm text-muted-foreground mb-2 leading-relaxed">
                              {translatedEvent.description}
                            </p>
                            
                            {/* Location */}
                            <div className="flex items-start gap-1.5">
                              <MapPin className="w-3.5 h-3.5 blue-text mt-0.5 flex-shrink-0" />
                              <span className="font-sans text-xs blue-text font-medium">
                                {translatedEvent.location}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="max-w-4xl mx-auto px-4">
        <div className="border-t border-primary opacity-20"></div>
      </div>


      {/* Weather Information Section */}
      <section className="py-20 cream-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Thermometer className="w-12 h-12 blue-text mx-auto mb-6" />
            <h2 className="font-serif text-4xl md:text-5xl font-semibold blue-text mb-4">
              {t('schedule.weather.title')}
            </h2>
            <p className="font-sans text-lg blue-text opacity-80">
              {t('schedule.weather.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Average Highs */}
            <div className="border-2 border-primary cream-bg p-8 text-center">
              <div className="text-5xl font-serif font-bold blue-text mb-3">
                25-27°C
              </div>
              <div className="text-lg font-sans blue-text opacity-80">
                Average Highs
              </div>
            </div>

            {/* Average Lows */}
            <div className="border-2 border-primary cream-bg p-8 text-center">
              <div className="text-5xl font-serif font-bold blue-text mb-3">
                ~17°C
              </div>
              <div className="text-lg font-sans blue-text opacity-80">
                Average Lows (Evening)
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Venue Map Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <MapPin className="w-12 h-12 blue-text mx-auto mb-6" />
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
              {t('schedule.venue.title')}
            </h2>
            <p className="font-sans text-lg text-muted-foreground mb-2">
              {scheduleData.venue.name}
            </p>
            
            {/* Venue Contact Information */}
            <div className="mt-6 space-y-2 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 text-base text-muted-foreground">
                <MapPin className="w-5 h-5 blue-text flex-shrink-0" />
                <span className="font-sans">Talici Hill - Cendulica, Brca, Sutomore 85355, Montenegro</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-base text-muted-foreground">
                <Phone className="w-5 h-5 blue-text flex-shrink-0" />
                <a href="tel:+38268068843" className="font-sans blue-text hover:underline">
                  +382 68 068 843
                </a>
              </div>
              <div className="flex items-center justify-center gap-2 text-base text-muted-foreground">
                <Globe className="w-5 h-5 blue-text flex-shrink-0" />
                <a 
                  href="https://talicihill.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans blue-text hover:underline"
                >
                  talicihill.com
                </a>
              </div>
            </div>
          </div>

          {/* Map Embed */}
          <div className="bg-white shadow-xl overflow-hidden border-2 border-border">
            <div className="aspect-video w-full">
              <iframe
                title="Venue Location"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src="https://maps.google.com/maps?q=42.13051886690426,19.067913878427394&t=&z=15&ie=UTF8&iwloc=&output=embed"
              />
            </div>
            <div className="p-6 cream-bg">
              <a 
                href="https://www.google.com/maps/dir/?api=1&destination=42.13051886690426,19.067913878427394"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 blue-text hover:underline font-sans font-medium transition-colors"
              >
                <MapPin className="w-5 h-5" />
                {t('schedule.venue.getDirections')}
              </a>
              <span className="mx-4 text-muted-foreground">|</span>
              <a 
                href="https://talicihill.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="blue-text hover:underline font-sans font-medium transition-colors"
              >
                {t('schedule.venue.visitWebsite')} →
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
